import { DatePicker } from "@mantine/dates";
import useMedia, { MediaSizes } from "../../hooks/useMedia";
import { useStoreState } from "pullstate";
import Store from "../../utils/Store";

const DAY_SIZE_BY_PICKER_SIZE = {
  xs: 34,
  sm: 38,
  md: 46,
  lg: 58,
  xl: 66,
};

const HEADER_SIZE_BY_PICKER_SIZE = {
  xs: 32,
  sm: 40,
  md: 44,
  lg: 50,
  xl: 54,
};

const SPACING_BY_PICKER_SIZE = {
  xs: 10,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
};

const getPickerDimension = (size, sizeMap, fallback) => {
  if (typeof size === "number" && Number.isFinite(size)) return size;
  return sizeMap[size] ?? fallback;
};

const getPixelValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.endsWith("px")) {
    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
  return undefined;
};

const SingleDateInput = ({
  placeholder,
  value,
  onChange,
  tooltip,
  classNames = "",
  inputFormat,
  styles: customStyles = {},
  options = {} /* Can pass options like maxDate, disabled, required */,
  hasBorder = true,
  bgColor = "",
  highlightToday = false,
  dayStyle = undefined,
  useTimezone = true,
}) => {
  const isMobile = useMedia([MediaSizes.MOBILE], [true], false);
  const { isDark } = useStoreState(Store, (s) => ({
    isDark: s.isDarkMode,
  }));

  const parseDateValue = (val) => {
    if (!val) return null;
    if (typeof val !== "string") return val;
    if (!useTimezone) {
      const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
      if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
    }
    return new Date(val);
  };

  const dateValue = parseDateValue(value);
  const { dayStyle: optionsDayStyle, ...datePickerOptions } = options;

  const today = new Date();
  const getDayStyle = (date, modifiers) => {
    const customDayStyle = {
      ...(typeof optionsDayStyle === "function" ? optionsDayStyle(date, modifiers) : {}),
      ...(typeof dayStyle === "function" ? dayStyle(date, modifiers) : {}),
    };
    const isToday =
      highlightToday &&
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();

    if (!isToday) {
      return customDayStyle;
    }

    const isSelectedDay = modifiers?.selected || modifiers?.selectedInRange;
    const todayHighlightStyle = {
      boxShadow: "inset 0 0 0 2px #00A9A7",
      color: "#1F2933",
    };

    return {
      ...customDayStyle,
      ...(!isSelectedDay ? todayHighlightStyle : {}),
      boxShadow: isSelectedDay
        ? "inset 0 0 0 2px #F6F7F7"
        : todayHighlightStyle.boxShadow,
      fontWeight: 700,
    };
  };

  const pickerSize = options?.size ?? "sm";
  const baseDaySize = getPickerDimension(pickerSize, DAY_SIZE_BY_PICKER_SIZE, 38);
  const dayWidth = getPixelValue(customStyles.day?.width) ?? baseDaySize;
  const dayHeight =
    getPixelValue(customStyles.day?.height) ??
    getPixelValue(customStyles.day?.minHeight) ??
    baseDaySize;
  const headerSize = getPickerDimension(pickerSize, HEADER_SIZE_BY_PICKER_SIZE, 40);
  const calendarGap = getPickerDimension(pickerSize, SPACING_BY_PICKER_SIZE, 12);
  const amountOfMonths = options?.amountOfMonths ?? 1;
  const singleMonthWidth = dayWidth * 7;
  const calendarWidth =
    singleMonthWidth * amountOfMonths + calendarGap * (amountOfMonths - 1);
  const calendarHeight = headerSize + calendarGap + dayHeight + dayHeight * 6;

  const style = isDark
    ? {
        input: {
          fontSize: "14px",
          color: "#F6F7F7",
          border: "1px solid #646B72", // bg-teal-400
          borderRadius: "6px",
          backgroundColor: "#29333E", // bg-gray-900
          "&:focus-within": {
            borderColor: "#646B72", // bg-gray-600
          },
          "::placeholder": {
            color: "#9ca3af",
          },
        },
      }
    : {
        input: {
          fontSize: "14px",
          border: "1px solid #646B72", // bg-gray-600
          borderRadius: "6px",
          backgroundColor: "#F6F7F7", // bg-gray-100
          "&:focus-within": {
            borderColor: "#646B72", // bg-gray-600
          },
          "::placeholder": {
            color: "#646B72",
          },
        },
      };

  if (!hasBorder) {
    style.input.border = "none";
  }
  if (bgColor) {
    style.input.backgroundColor = bgColor;
  }

  const datePickerStyles = {
    ...customStyles,
    root: {
      position: "relative",
      ...customStyles.root,
    },
    wrapper: {
      ...customStyles.wrapper,
    },
    icon: {
      ...customStyles.icon,
    },
    input: {
      ...style.input,
      ...customStyles.input,
    },
    dropdown: {
      backgroundColor: "#F6F7F7", // bg-gray-100
      border: "1px solid #646B72", // bg-gray-600
      boxSizing: "border-box",
      transition: "none !important",
      marginTop: "-5px",
      display: "flex",
      justifyContent: "center",
      position: "fixed",
      "& > div": {
        width: "100%",
      },
      ...customStyles.dropdown,
    },
    calendarBase: {
      alignItems: "flex-start",
      width: calendarWidth,
      minWidth: calendarWidth,
      maxWidth: calendarWidth,
      minHeight: calendarHeight,
      gap: calendarGap,
      ...customStyles.calendarBase,
    },
    calendarHeader: {
      alignItems: "center",
      width: singleMonthWidth,
      minWidth: singleMonthWidth,
      maxWidth: singleMonthWidth,
      marginBottom: calendarGap,
      ...customStyles.calendarHeader,
    },
    calendarHeaderControl: {
      flex: `0 0 ${headerSize}px`,
      width: headerSize,
      minWidth: headerSize,
      height: headerSize,
      minHeight: headerSize,
      "&:hover": {
        backgroundColor: "inherit",
      },
      ...customStyles.calendarHeaderControl,
    },
    calendarHeaderLevel: {
      minWidth: 0,
      height: headerSize,
      paddingLeft: 0,
      paddingRight: 0,
      whiteSpace: "nowrap",
      "&:hover": {
        backgroundColor: "inherit",
      },
      ...customStyles.calendarHeaderLevel,
    },
    monthPickerControlActive: {
      backgroundColor: "#00A9A7",
      "&:hover": {
        backgroundColor: "#00A9A7",
      },
      ...customStyles.monthPickerControlActive,
    },
    yearPickerControlActive: {
      backgroundColor: "#00A9A7",
      "&:hover": {
        backgroundColor: "#00A9A7",
      },
      ...customStyles.yearPickerControlActive,
    },
    month: {
      width: singleMonthWidth,
      minWidth: singleMonthWidth,
      maxWidth: singleMonthWidth,
      tableLayout: "fixed",
      ...customStyles.month,
    },
    day: {
      width: dayWidth,
      minWidth: dayWidth,
      maxWidth: dayWidth,
      height: dayHeight,
      minHeight: dayHeight,
      maxHeight: dayHeight,
      lineHeight: `${dayHeight}px`,
      "&[data-selected]": {
        backgroundColor: "#00A9A7",
      },
      "&[data-in-range]": {
        backgroundColor: "#D9DBDD",
      },
      "&[data-first-in-range]": {
        backgroundColor: "#00A9A7",
      },
      "&[data-last-in-range]": {
        backgroundColor: "#00A9A7",
      },
      ...customStyles.day,
    },
    cell: {
      width: dayWidth,
      minWidth: dayWidth,
      maxWidth: dayWidth,
      height: dayHeight,
      minHeight: dayHeight,
      maxHeight: dayHeight,
      ...customStyles.cell,
    },
    weekdayCell: {
      width: dayWidth,
      minWidth: dayWidth,
      maxWidth: dayWidth,
      ...customStyles.weekdayCell,
    },
  };

  return (
    <div
      title={tooltip}
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={classNames}
    >
      <DatePicker
        inputFormat={inputFormat}
        icon={
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.3571 12C11.7107 12 12 12.3094 12 12.6875V16.8125C12 17.1906 11.7107 17.5 11.3571 17.5H7.5C7.14643 17.5 6.85714 17.1906 6.85714 16.8125V12.6875C6.85714 12.3094 7.14643 12 7.5 12H11.3571ZM8.14286 1C8.67723 1 9.10714 1.46191 9.10714 2.03125V3.75H14.8929V2.03125C14.8929 1.46191 15.3228 1 15.8571 1C16.3915 1 16.8214 1.46191 16.8214 2.03125V3.75H18.4286C19.8469 3.75 21 4.98105 21 6.5V20.25C21 21.7668 19.8469 23 18.4286 23H5.57143C4.15112 23 3 21.7668 3 20.25V6.5C3 4.98105 4.15112 3.75 5.57143 3.75H7.17857V2.03125C7.17857 1.46191 7.60848 1 8.14286 1ZM19.0714 9.25H4.92857V20.25C4.92857 20.6281 5.21625 20.9375 5.57143 20.9375H18.4286C18.7821 20.9375 19.0714 20.6281 19.0714 20.25V9.25Z"
              fill={isDark ? "#F6F7F7" : "#474F58"}
            />
          </svg>
        }
        placeholder={placeholder}
        value={dateValue}
        onChange={onChange}
        dayStyle={getDayStyle}
        styles={datePickerStyles}
        {...datePickerOptions}
      />
    </div>
  );
};

export default SingleDateInput;
